import React, { useEffect, useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Logo } from '@/components/Logo';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { NAV } from '@/constants/testIds';

export const Navbar = () => {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const LINKS = [
    { to: '/followers', label: t('nav.followers'), testId: NAV.linkFollower },
    { to: '/providers', label: t('nav.providers'), testId: NAV.linkProvider },
    { to: '/pricing', label: t('nav.pricing'), testId: NAV.linkPricing },
    { to: '/guides', label: t('nav.guides'), testId: NAV.linkGuides },
    { to: '/faq', label: t('nav.faq'), testId: NAV.linkFaq },
    { to: '/contact', label: t('nav.contact'), testId: NAV.linkContact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'}`}>
      <nav className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6 lg:px-8">
        <Logo />
        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              className={({ isActive }) => `text-sm font-medium transition-colors hover:text-brand ${isActive ? 'text-brand' : 'text-white/70'}`}
            >
              {l.label}
            </NavLink>
          ))}
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          <LanguageSwitcher />
          <Link to="/pricing" data-testid={NAV.ctaTrial} className="btn-primary text-sm">
            {t('nav.cta')}
          </Link>
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          <button
            className="text-white p-2"
            onClick={() => setOpen((v) => !v)}
            data-testid={NAV.mobileToggle}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            data-testid={NAV.mobileMenu}
            className="lg:hidden overflow-hidden border-t border-white/10 bg-black/95 backdrop-blur-xl"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `rounded-lg px-3 py-3 text-base font-medium ${isActive ? 'bg-brand/10 text-brand' : 'text-white/80'}`}
                >
                  {l.label}
                </NavLink>
              ))}
              <Link to="/pricing" onClick={() => setOpen(false)} className="btn-primary mt-2 justify-center">
                {t('nav.cta')}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
